import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const chipStyle = {
  padding: '0.25rem 0.85rem',
  fontSize: '0.8rem',
  borderRadius: '999px',
  border: '1px solid #28b6c8',
  background: '#28b6c8',
  color: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.35rem',
  fontWeight: 600,
  letterSpacing: '0.02em'
};

const iconStyle = {
  width: '1rem',
  height: '1rem',
  borderRadius: '999px',
  background: 'rgba(255, 255, 255, 0.25)',
  color: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.7rem'
};

function InfoChip({ label = 'Info', children }) {
  const [open, setOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const chipRef = useRef(null);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      if (typeof window === 'undefined') return;
      const rect = chipRef.current?.getBoundingClientRect();
      if (!rect) return;
      const spacing = 8;
      const padding = 12;
      const maxWidth = 300;
      let left = rect.left;
      if (left + maxWidth > window.innerWidth - padding) {
        left = window.innerWidth - maxWidth - padding;
      }
      left = Math.max(padding, left);
      setPopoverPos({
        top: rect.bottom + spacing,
        left
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  return (
    <span
      className="info-chip"
      ref={chipRef}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', zIndex: open ? 1000 : 'auto' }}
    >
      <button
        type="button"
        className="ghost-button"
        style={chipStyle}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span style={iconStyle}>i</span>
        <span>{label}</span>
      </button>
      {open && hasMounted && createPortal(
        <div
          className="info-chip-popover"
          style={{
            position: 'fixed',
            top: popoverPos.top,
            left: popoverPos.left,
            zIndex: 9999,
            minWidth: '220px',
            maxWidth: '300px',
            width: 'min(300px, calc(100vw - 24px))',
            borderRadius: '10px',
            border: '1px solid #d4d4d8',
            background: '#fff',
            boxShadow: '0 12px 30px rgba(15,23,42,0.2)',
            padding: '0.75rem'
          }}
        >
          <p className="status-text" style={{ margin: 0 }}>{children}</p>
          <div style={{ textAlign: 'right', marginTop: '0.5rem' }}>
            <button
              type="button"
              className="ghost-button"
              style={chipStyle}
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}
    </span>
  );
}

export default InfoChip;
